/**
 * CUSTOMER FIX Stage 7 — one-time sessionStorage → localStorage cart migration.
 * Idempotent: skips keys already present in localStorage.
 */

import { rebuildCartRegistryFromStorage } from './cartRegistry.js';
import { localStorageHasAnyCartPayload, readPersistedCartQuantity } from './cartPersistenceRead.js';
import { readRawCartRegistry } from './cartRegistryStorage.js';

const STORAGE_PREFIX = 'rpapp-customer.shop.cart';
const MIGRATION_FLAG_KEY = 'rpapp-customer.shop.cart.migrated-to-local-v1';

function safeSessionStorage(): Storage | null {
  if (typeof window === 'undefined' || !('sessionStorage' in window)) {
    return null;
  }
  try {
    const probeKey = `${STORAGE_PREFIX}:migrate-probe`;
    window.sessionStorage.setItem(probeKey, '1');
    window.sessionStorage.removeItem(probeKey);
    return window.sessionStorage;
  } catch {
    return null;
  }
}

function safeLocalStorage(): Storage | null {
  if (typeof window === 'undefined' || !('localStorage' in window)) {
    return null;
  }
  try {
    const probeKey = `${STORAGE_PREFIX}:migrate-probe`;
    window.localStorage.setItem(probeKey, '1');
    window.localStorage.removeItem(probeKey);
    return window.localStorage;
  } catch {
    return null;
  }
}

let migrationDone = false;

/** Migrate legacy session carts into localStorage (Stage 7). Safe to call repeatedly. */
export function migrateSessionCartsToLocalStorage(): void {
  if (migrationDone) {
    return;
  }
  const local = safeLocalStorage();
  const session = safeSessionStorage();
  if (local === null) {
    migrationDone = true;
    return;
  }

  if (local.getItem(MIGRATION_FLAG_KEY) === '1') {
    migrationDone = true;
    return;
  }

  const prefix = `${STORAGE_PREFIX}:`;
  if (session !== null) {
    const keysToRemove: string[] = [];
    for (let i = 0; i < session.length; i += 1) {
      const key = session.key(i);
      if (key === null || !key.startsWith(prefix)) {
        continue;
      }
      const raw = session.getItem(key);
      if (raw === null) {
        continue;
      }
      if (local.getItem(key) === null) {
        try {
          local.setItem(key, raw);
        } catch {
          continue;
        }
      }
      keysToRemove.push(key);
    }
    for (const key of keysToRemove) {
      try {
        session.removeItem(key);
      } catch {
        // ignore
      }
    }
  }

  try {
    local.setItem(MIGRATION_FLAG_KEY, '1');
  } catch {
    // ignore
  }

  const registryHasNonemptyRows = readRawCartRegistry().entries.some(
    (entry) => entry.totalQuantity > 0,
  );
  if (!registryHasNonemptyRows && localStorageHasAnyCartPayload()) {
    rebuildCartRegistryFromStorage(readPersistedCartQuantity);
  }

  migrationDone = true;
}

/** @internal tests */
export function __resetCartMigrationFlagForTests(): void {
  migrationDone = false;
  const local = safeLocalStorage();
  if (local !== null) {
    local.removeItem(MIGRATION_FLAG_KEY);
  }
}
